import React from 'react';
import { getTeamLogoPath } from '../../utils/teams';
import {
  formatMatchTime,
  formatMatchTimeInVenueTimezone,
  isMatchLocked,
  getMatchStatusIcon,
  getPickResult,
} from '../../utils/matchHelpers';

/**
 * MatchCard component for displaying a single match
 */
const MatchCard = ({
  match,
  picks,
  adminMode,
  isViewingAsAdmin,
  editableScores,
  timeUntilLock,
  isSuperAdmin,
  showTeamMatchups,
  onPickChange,
  onScoreChange,
  onStatusChange,
  onTeamMatchupClick,
}) => {
  const locked = isMatchLocked(match);
  const pickResult = getPickResult(match);

  // Determine border color classes for team columns based on results
  let homeTeamClass = picks[match.id] === match.homeTeam.id ? 'selected' : '';
  let awayTeamClass = picks[match.id] === match.awayTeam.id ? 'selected' : '';

  if (match.status === 'completed') {
    if (!picks[match.id]) {
      // No pick made - both teams get dimmed/grayed out
      homeTeamClass += ' not-selected';
      awayTeamClass += ' not-selected';
    } else if (pickResult) {
      // Pick was made - apply result to picked team, dim the other
      if (picks[match.id] === match.homeTeam.id) {
        homeTeamClass += ` result-${pickResult.type}`;
        awayTeamClass += ' not-selected';
      } else {
        awayTeamClass += ` result-${pickResult.type}`;
        homeTeamClass += ' not-selected';
      }
    }
  }

  return (
    <div className={`match-card ${locked ? 'locked' : ''} ${adminMode ? 'admin-mode' : ''}`}>
      <div className="match-card-content">
        {/* Column 1: Lock Status Icon or Admin Status Dropdown */}
        <div className="lock-status-icon">
          {adminMode ? (
            <select
              value={match.status}
              onChange={(e) => onStatusChange(match.id, e.target.value)}
              className="status-dropdown"
              onClick={(e) => e.stopPropagation()}
            >
              <option value="scheduled">📅</option>
              <option value="live">🏈</option>
              <option value="awaiting_scores">⏱️</option>
              <option value="completed">✅</option>
              <option value="cancelled">❌</option>
            </select>
          ) : locked ? (
            <span className="lock-icon">{getMatchStatusIcon(match) || '🔒'}</span>
          ) : (
            <span className="countdown-compact">{timeUntilLock[match.id] || '...'}</span>
          )}
        </div>

        {/* Column 2: Match Info */}
        <div className="match-info-column">
          <div className="match-time-row">
            <div className="time-wrapper">
              <div className="match-time">{formatMatchTime(match.scheduledTime)}</div>
              {match.venue.timezone && (
                <div className="time-tooltip">
                  Venue time: {formatMatchTimeInVenueTimezone(match.scheduledTime, match.venue.timezone)}
                </div>
              )}
            </div>
            {!adminMode && !locked && (
              <div className="countdown-mobile">{timeUntilLock[match.id] || '...'}</div>
            )}
            {!adminMode && locked && (
              <div className="lock-icon-mobile">{getMatchStatusIcon(match) || '🔒'}</div>
            )}
            {adminMode && (
              <select
                value={match.status}
                onChange={(e) => onStatusChange(match.id, e.target.value)}
                className="status-dropdown status-dropdown-mobile"
                onClick={(e) => e.stopPropagation()}
              >
                <option value="scheduled">📅</option>
                <option value="live">🏈</option>
                <option value="awaiting_scores">⏱️</option>
                <option value="completed">✅</option>
                <option value="cancelled">❌</option>
              </select>
            )}
          </div>
          <div className="venue-info">
            <div className="venue-name-wrapper">
              <div className="venue-name">{match.venue.name}</div>
              {match.venue.historicalName && match.venue.historicalName !== match.venue.name && (
                <div className="venue-tooltip">Formerly: {match.venue.historicalName}</div>
              )}
            </div>
            <div className="venue-location">
              {match.venue.city}, {match.venue.state}
            </div>
          </div>
        </div>

        {/* Column 3: Home Team */}
        <div
          className={`team-pick-column ${homeTeamClass} ${adminMode ? 'admin-mode' : ''}`}
          onClick={() =>
            !adminMode && (!locked || isViewingAsAdmin) && onPickChange(match.id, match.homeTeam.id)
          }
        >
          <input
            type="radio"
            name={`match-${match.id}`}
            value={match.homeTeam.id}
            checked={picks[match.id] === match.homeTeam.id}
            onChange={() => onPickChange(match.id, match.homeTeam.id)}
            disabled={(locked && !isViewingAsAdmin) || adminMode}
            className="team-radio"
          />
          {isSuperAdmin && match.pickCounts !== undefined && (
            <span className="pick-count pick-count-home">{match.pickCounts[match.homeTeam.id] || 0}</span>
          )}
          {showTeamMatchups && (
            <button
              className="team-matchup-btn team-matchup-btn-home"
              onClick={(e) => { e.stopPropagation(); onTeamMatchupClick(match.homeTeam); }}
              title={`${match.homeTeam.name} season matches`}
            >...</button>
          )}
          <div className="team-main-content">
            <img
              src={getTeamLogoPath(match.homeTeam.id)}
              alt={match.homeTeam.name}
              className="team-logo"
            />
            <span className="team-name">{match.homeTeam.name}</span>
          </div>
          {adminMode ? (
            <input
              type="number"
              className="score-input home-score"
              value={editableScores[match.id]?.homeScore ?? match.homeScore ?? ''}
              onChange={(e) => onScoreChange(match.id, 'homeScore', e.target.value)}
              onClick={(e) => e.stopPropagation()}
              min="0"
            />
          ) : (
            ['live', 'awaiting_scores', 'completed'].includes(match.status) &&
            match.homeScore !== null && (
              <span className="team-score home-score">{match.homeScore}</span>
            )
          )}
        </div>

        {/* Column 4: VS Separator */}
        <div className="vs-separator">
          {(() => {
            const url = match.nrlMatchUrl || (() => {
              const year = new Date(match.scheduledTime).getFullYear();
              const homeSlug = match.homeTeam.name.toLowerCase().replace(/\s+/g, '-');
              const awaySlug = match.awayTeam.name.toLowerCase().replace(/\s+/g, '-');
              return `https://www.nrl.com/draw/nrl-premiership/${year}/round-${match.roundNumber}/${homeSlug}-v-${awaySlug}/`;
            })();
            return (
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="vs-link"
                onClick={(e) => e.stopPropagation()}
                title="View on NRL.com"
              >
                <span className="vs-text">V</span>
              </a>
            );
          })()}
        </div>

        {/* Column 5: Away Team */}
        <div
          className={`team-pick-column ${awayTeamClass} ${adminMode ? 'admin-mode' : ''}`}
          onClick={() =>
            !adminMode && (!locked || isViewingAsAdmin) && onPickChange(match.id, match.awayTeam.id)
          }
        >
          <input
            type="radio"
            name={`match-${match.id}`}
            value={match.awayTeam.id}
            checked={picks[match.id] === match.awayTeam.id}
            onChange={() => onPickChange(match.id, match.awayTeam.id)}
            disabled={(locked && !isViewingAsAdmin) || adminMode}
            className="team-radio"
          />
          {adminMode ? (
            <input
              type="number"
              className="score-input away-score"
              value={editableScores[match.id]?.awayScore ?? match.awayScore ?? ''}
              onChange={(e) => onScoreChange(match.id, 'awayScore', e.target.value)}
              onClick={(e) => e.stopPropagation()}
              min="0"
            />
          ) : (
            ['live', 'awaiting_scores', 'completed'].includes(match.status) &&
            match.awayScore !== null && (
              <span className="team-score away-score">{match.awayScore}</span>
            )
          )}
          {isSuperAdmin && match.pickCounts !== undefined && (
            <span className="pick-count pick-count-away">{match.pickCounts[match.awayTeam.id] || 0}</span>
          )}
          {showTeamMatchups && (
            <button
              className="team-matchup-btn team-matchup-btn-away"
              onClick={(e) => { e.stopPropagation(); onTeamMatchupClick(match.awayTeam); }}
              title={`${match.awayTeam.name} season matches`}
            >...</button>
          )}
          <div className="team-main-content">
            <img
              src={getTeamLogoPath(match.awayTeam.id)}
              alt={match.awayTeam.name}
              className="team-logo"
            />
            <span className="team-name">{match.awayTeam.name}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(MatchCard);
